import React from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Icon from '@mui/material/Icon';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { alpha, useTheme as useMuiTheme } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import { accountingApi } from '../../api/accountingApi';
import { trMoney } from '../../utils/accountingFormat';
import { SessionDebtPayDialog } from 'sectors/pilates/payments/modal/tabs/SessionPaymentTab';
import { PaymentReceiveModal } from 'sectors/pilates/members/components/paymentReceive/PaymentReceiveModal';
import { SmsSendModal } from 'sectors/pilates/members/components/MemberActionsMenu/SmsSendModal';
import { MemberDetailModal } from 'sectors/pilates/members/modal/MemberDetailModal/MemberDetailModal';
import { PaymentDetailModal } from 'sectors/pilates/members/modal/MemberDetailModal/tabs/components/PaymentsTab/PaymentDetailModal';
import { PaymentEditModal } from 'sectors/pilates/members/components/paymentReceive/PaymentEditModal';
import { membersApi } from 'sectors/pilates/members/api/membersApi';
import { staffApi } from 'sectors/pilates/staff/api/staffApi';
import { smsApi } from 'sectors/pilates/sms/api/smsApi';

function textOrDash(value) {
  const text = String(value ?? '').trim();
  return text || '-';
}

function buildReceivableDescription(row) {
  if (row.paymentType === 'PACKAGE') {
    return row.packageName ? `Paket borcu: ${row.packageName}` : 'Paket borcu';
  }

  if (row.paymentType === 'SESSION') {
    const parts = ['Seans borcu', row.activityName, row.dayISO, row.trainerName].filter(Boolean);
    return parts.join(' • ');
  }

  return row.packageName || row.activityName || 'Bekleyen odeme';
}

function buildRecentPaymentDescription(row) {
  const label =
    row.packageName ||
    (row.paymentType === 'PACKAGE'
      ? 'Paket odemesi'
      : row.paymentType === 'SESSION'
      ? row.activityName || 'Seans odemesi'
      : 'Genel odeme');

  return [label, row.method, row.paidDate].filter(Boolean).join(' • ');
}

function mapIncomeSessionRowToPaymentDialogRow(row) {
  return {
    sessionId: row.sessionId ?? null,
    dayISO: row.dayISO ?? null,
    startMin: row.startMin ?? null,
    durationMin: row.durationMin ?? null,
    activityName: row.activityName ?? null,
    trainerId: row.trainerId ?? null,
    trainerName: row.trainerName ?? null,
    sessionParticipantId: row.sessionParticipantId ?? row.sessionId ?? null,
    memberId: row.memberId ?? null,
    fullName: row.fullName ?? null,
    phone: row.phone ?? null,
    totalTry: row.amountTry ?? 0,
    paidTry: 0,
    sessionDebtTry: Number(row.amountTry || 0),
    paymentStatus: 'PENDING',
  };
}

function mapCollectedPaymentToModal(row) {
  return {
    id: row.paymentId,
    fullName: row.fullName,
    payerName: row.payerName,
    amountTry: row.amountTry,
    paidDate: row.paidDate,
    method: row.method,
    note: row.note,
    purpose: row.paymentType,
    receivedByName: row.receivedByName,
    approvedByName: row.approvedByName,
    approvedAt: row.approvedAt,
    createdAt: row.createdAt,
    planNameSnapshot: row.packageName,
    planStatus: row.planStatus,
  };
}

function TrainerCollectionCard({ border, from, to }) {
  const theme = useMuiTheme();
  const [trainers, setTrainers] = React.useState([]);
  const [selectedTrainerId, setSelectedTrainerId] = React.useState('');
  const [summary, setSummary] = React.useState({
    totalCollectedTry: 0,
    expectedPaymentTry: 0,
  });
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;

    staffApi
      .list()
      .then((list) => {
        if (cancelled) return;
        const items = Array.isArray(list) ? list : [];
        setTrainers(items);
        if (!selectedTrainerId && items.length > 0) {
          setSelectedTrainerId(String(items[0].id));
        }
      })
      .catch(() => {
        if (!cancelled) setTrainers([]);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedTrainerId]);

  React.useEffect(() => {
    if (!selectedTrainerId) return;
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        const res = await accountingApi.income({
          from,
          to,
          trainerId: Number(selectedTrainerId),
          limit: 200,
        });
        if (cancelled) return;
        setSummary({
          totalCollectedTry: Number(res?.summary?.totalCollectedTry || 0),
          expectedPaymentTry: Number(res?.summary?.expectedPaymentTry || 0),
        });
      } catch (error) {
        if (!cancelled) {
          setSummary({
            totalCollectedTry: 0,
            expectedPaymentTry: 0,
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [from, selectedTrainerId, to]);

  return (
    <Card
      sx={{
        borderRadius: '18px',
        border: `1px solid ${border}`,
        boxShadow: 'none',
        overflow: 'hidden',
        height: '100%',
      }}
    >
      <MDBox sx={{ px: 1.25, py: 1.05, display: 'grid', gap: 1 }}>
        <MDBox
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1,
          }}
        >
          <MDBox>
            <MDTypography variant="button" fontWeight="bold">
              Egitmene Gore Tahsilat
            </MDTypography>
            <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
              Alinan ve beklenen odemeyi ayni yerde gor.
            </MDTypography>
          </MDBox>

          <TextField
            select
            size="small"
            value={selectedTrainerId}
            onChange={(e) => setSelectedTrainerId(e.target.value)}
            sx={{ minWidth: 220 }}
          >
            {trainers.map((trainer) => (
              <MenuItem key={trainer.id} value={String(trainer.id)}>
                {trainer.fullName || `#${trainer.id}`}
              </MenuItem>
            ))}
          </TextField>
        </MDBox>

        <MDBox sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
          <MDBox
            sx={{
              p: 1,
              borderRadius: '14px',
              bgcolor: alpha(theme.palette.success.main, 0.08),
              border: `1px solid ${alpha(theme.palette.success.main, 0.16)}`,
            }}
          >
            <MDTypography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
              Alinan odeme
            </MDTypography>
            <MDTypography fontWeight="bold">
              {loading ? '...' : trMoney(summary.totalCollectedTry)}
            </MDTypography>
          </MDBox>
          <MDBox
            sx={{
              p: 1,
              borderRadius: '14px',
              bgcolor: alpha(theme.palette.warning.main, 0.08),
              border: `1px solid ${alpha(theme.palette.warning.main, 0.16)}`,
            }}
          >
            <MDTypography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
              Alinmasi gereken
            </MDTypography>
            <MDTypography fontWeight="bold">
              {loading ? '...' : trMoney(summary.expectedPaymentTry)}
            </MDTypography>
          </MDBox>
          <MDBox
            sx={{
              p: 1,
              borderRadius: '14px',
              bgcolor: alpha(theme.palette.info.main, 0.08),
              border: `1px solid ${alpha(theme.palette.info.main, 0.16)}`,
            }}
          >
            <MDTypography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
              Tahmini toplam
            </MDTypography>
            <MDTypography fontWeight="bold">
              {loading ? '...' : trMoney(summary.totalCollectedTry + summary.expectedPaymentTry)}
            </MDTypography>
          </MDBox>
        </MDBox>
      </MDBox>
    </Card>
  );
}

function SectionCard({
  title,
  subtitle,
  rows,
  emptyText,
  renderActions,
  renderDescription,
  amountColor,
}) {
  return (
    <Card
      sx={{
        borderRadius: '18px',
        border: '1px solid rgba(15,23,42,0.08)',
        boxShadow: 'none',
        overflow: 'hidden',
        height: '100%',
      }}
    >
      <MDBox sx={{ px: 1.25, py: 1.05 }}>
        <MDTypography variant="button" fontWeight="bold">
          {title}
        </MDTypography>
        <MDTypography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
          {subtitle}
        </MDTypography>
      </MDBox>

      <Divider />

      <MDBox sx={{ p: 1.25, display: 'grid', gap: 0.8 }}>
        {!rows?.length ? (
          <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
            {emptyText}
          </MDTypography>
        ) : (
          rows.map((row, index) => (
            <MDBox
              key={row.id || row.paymentId || row.staffId || `${title}-${index}`}
              sx={{
                p: 1,
                borderRadius: '14px',
                border: '1px solid rgba(15,23,42,0.06)',
                bgcolor: 'rgba(248,250,252,0.72)',
                display: 'grid',
                gap: 0.8,
              }}
            >
              <MDBox
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: 1,
                }}
              >
                <MDBox sx={{ minWidth: 0 }}>
                  <MDTypography variant="button" fontWeight="bold">
                    {textOrDash(row.fullName || row.payerName || row.title || row.staffName)}
                  </MDTypography>
                  <MDTypography
                    variant="caption"
                    sx={{ color: 'text.secondary', display: 'block' }}
                  >
                    {renderDescription(row)}
                  </MDTypography>
                </MDBox>

                {row.amountTry != null ? (
                  <Chip
                    size="small"
                    color={amountColor}
                    variant="outlined"
                    label={trMoney(row.amountTry)}
                    sx={{ fontWeight: 800 }}
                  />
                ) : null}
              </MDBox>

              {renderActions ? renderActions(row) : null}
            </MDBox>
          ))
        )}
      </MDBox>
    </Card>
  );
}

export default function AccountingOverviewTab({ border, data, income, expenses, dateRange }) {
  const [selectedReceivable, setSelectedReceivable] = React.useState(null);
  const [sessionPayOpen, setSessionPayOpen] = React.useState(false);
  const [smsOpen, setSmsOpen] = React.useState(false);
  const [smsMember, setSmsMember] = React.useState(null);
  const [smsDefaultMessage, setSmsDefaultMessage] = React.useState('');
  const [packagePayOpen, setPackagePayOpen] = React.useState(false);
  const [packagePayMember, setPackagePayMember] = React.useState(null);
  const [memberDetailOpen, setMemberDetailOpen] = React.useState(false);
  const [selectedMember, setSelectedMember] = React.useState(null);
  const [selectedPayment, setSelectedPayment] = React.useState(null);
  const [detailOpen, setDetailOpen] = React.useState(false);
  const [editOpen, setEditOpen] = React.useState(false);

  const refreshAll = React.useCallback(async () => {
    await Promise.all(
      [income?.refetch?.(), expenses?.refetch?.(), data?.refetch?.()].filter(Boolean)
    );
  }, [data, expenses, income]);

  const handleOpenMember = React.useCallback((row) => {
    if (!row?.memberId) return;
    const fullName = row.fullName || '';
    const parts = fullName.trim().split(' ');
    setSelectedMember({
      id: row.memberId,
      fullName: row.fullName || '',
      name: parts[0] || '',
      surname: parts.slice(1).join(' ') || '',
      phone: row.phone || row.payerPhone || '',
    });
    setMemberDetailOpen(true);
  }, []);

  const handleReceivePayment = React.useCallback((row) => {
    if (!row) return;

    if (row.paymentType === 'SESSION') {
      setSelectedReceivable(row);
      setSessionPayOpen(true);
      return;
    }

    if (row.paymentType === 'PACKAGE') {
      setPackagePayMember({
        id: row.memberId,
        fullName: row.fullName,
        phone: row.phone,
        memberPlanId: row.memberPlanId,
      });
      setPackagePayOpen(true);
    }
  }, []);

  const handleSendReminder = React.useCallback((row) => {
    if (!row) return;
    const firstName = String(row.fullName || row.payerName || '')
      .trim()
      .split(' ')[0];

    const member = {
      id: row.memberId ?? null,
      fullName: row.fullName || row.payerName || 'Uye',
      phone: row.phone || row.payerPhone || '',
    };

    const body =
      row.paymentType === 'PACKAGE'
        ? `${firstName}, ${trMoney(row.amountTry || 0)} tutarinda bekleyen paket odemen bulunuyor.`
        : `${firstName}, ${trMoney(row.amountTry || 0)} tutarinda bekleyen seans odemen bulunuyor.`;

    setSmsMember(member);
    setSmsDefaultMessage(body);
    setSmsOpen(true);
  }, []);

  if (data?.error) {
    return <Alert severity="error">Muhasebe ozeti yuklenemedi.</Alert>;
  }

  return (
    <>
      <MDBox sx={{ display: 'grid', gap: 1.5 }}>
        <Grid container spacing={1.5}>
          <Grid item xs={12}>
            <TrainerCollectionCard
              border={border}
              from={dateRange?.startDate}
              to={dateRange?.endDate}
            />
          </Grid>

          <Grid item xs={12} lg={6}>
            <SectionCard
              title="Bekleyen Tahsilatlar"
              subtitle="Gelirler sekmesindeki kayitlarla ayni liste"
              rows={data.pendingReceivables}
              emptyText="Bekleyen tahsilat yok."
              amountColor="warning"
              renderDescription={buildReceivableDescription}
              renderActions={(row) => (
                <MDBox sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
                  <Button
                    size="small"
                    startIcon={<Icon fontSize="small">payments</Icon>}
                    onClick={() => handleReceivePayment(row)}
                  >
                    Odeme al
                  </Button>
                  <Button
                    size="small"
                    startIcon={<Icon fontSize="small">campaign</Icon>}
                    onClick={() => handleSendReminder(row)}
                    disabled={!row.phone && !row.payerPhone}
                  >
                    Sms gonder
                  </Button>
                  <Button
                    size="small"
                    startIcon={<Icon fontSize="small">person</Icon>}
                    onClick={() => handleOpenMember(row)}
                    disabled={!row.memberId}
                  >
                    Uye detayi
                  </Button>
                </MDBox>
              )}
            />
          </Grid>

          <Grid item xs={12} lg={6}>
            <SectionCard
              title="Bekleyen Giderler"
              subtitle="Vadesi gelen acik giderler"
              rows={data.pendingExpenses}
              emptyText="Bekleyen gider yok."
              amountColor="error"
              renderDescription={(row) =>
                [row.category, row.dueDate ? `Vade ${row.dueDate}` : null]
                  .filter(Boolean)
                  .join(' • ')
              }
            />
          </Grid>

          <Grid item xs={12} lg={6}>
            <SectionCard
              title="Maasa Dahil Personel"
              subtitle="Hesaplamaya hazir ekip listesi"
              rows={data.payrollReady}
              emptyText="Maasa dahil personel yok."
              renderDescription={(row) => [row.role, row.payrollLabel].filter(Boolean).join(' • ')}
            />
          </Grid>

          <Grid item xs={12} lg={6}>
            <SectionCard
              title="Son Alinan Odemeler"
              subtitle="Detay ve duzenleme aksiyonlari acik"
              rows={data.recentPayments}
              emptyText="Kaydedilmis odeme yok."
              amountColor="success"
              renderDescription={buildRecentPaymentDescription}
              renderActions={(row) => (
                <MDBox sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
                  <Button
                    size="small"
                    startIcon={<Icon fontSize="small">visibility</Icon>}
                    onClick={() => {
                      setSelectedPayment(mapCollectedPaymentToModal(row));
                      setDetailOpen(true);
                    }}
                  >
                    Detay
                  </Button>
                  <Button
                    size="small"
                    startIcon={<Icon fontSize="small">edit</Icon>}
                    onClick={() => {
                      setSelectedPayment(mapCollectedPaymentToModal(row));
                      setEditOpen(true);
                    }}
                  >
                    Duzenle
                  </Button>
                  <Button
                    size="small"
                    startIcon={<Icon fontSize="small">person</Icon>}
                    onClick={() => handleOpenMember(row)}
                    disabled={!row.memberId}
                  >
                    Uye detayi
                  </Button>
                </MDBox>
              )}
            />
          </Grid>
        </Grid>
      </MDBox>

      <SessionDebtPayDialog
        open={sessionPayOpen}
        onClose={() => {
          setSessionPayOpen(false);
          setSelectedReceivable(null);
        }}
        row={
          selectedReceivable?.paymentType === 'SESSION'
            ? mapIncomeSessionRowToPaymentDialogRow(selectedReceivable)
            : null
        }
        onPaid={async () => {
          setSessionPayOpen(false);
          setSelectedReceivable(null);
          await refreshAll();
        }}
      />

      <PaymentReceiveModal
        open={packagePayOpen}
        member={packagePayMember}
        initialStep="PLAN"
        onClose={() => {
          setPackagePayOpen(false);
          setPackagePayMember(null);
        }}
        onSuccess={async () => {
          setPackagePayOpen(false);
          setPackagePayMember(null);
          await refreshAll();
        }}
      />

      <SmsSendModal
        open={smsOpen}
        onClose={() => {
          setSmsOpen(false);
          setSmsMember(null);
          setSmsDefaultMessage('');
        }}
        member={smsMember}
        companyName="Solisup"
        defaultMessage={smsDefaultMessage}
        onSend={async ({ memberId, phone, message }) => {
          const payload = {
            memberId: memberId ?? null,
            memberName: smsMember?.fullName || null,
            message,
            phone,
          };

          const res = await smsApi.sendManual(payload);
          if (res?.ok === false) {
            const err =
              res?.error ||
              (Array.isArray(res?.errors) ? res.errors[0] : null) ||
              res?.message ||
              'SMS gonderilemedi.';
            throw new Error(err);
          }

          return res;
        }}
      />

      <MemberDetailModal
        open={memberDetailOpen}
        member={selectedMember}
        onClose={() => {
          setMemberDetailOpen(false);
          setSelectedMember(null);
        }}
      />

      <PaymentDetailModal
        open={detailOpen}
        payment={selectedPayment}
        onClose={() => {
          setDetailOpen(false);
          setSelectedPayment(null);
        }}
      />

      <PaymentEditModal
        open={editOpen}
        payment={selectedPayment}
        memberLabel={selectedPayment?.fullName || selectedPayment?.payerName || 'Odeme'}
        onClose={() => {
          setEditOpen(false);
          setSelectedPayment(null);
        }}
        onSave={async (paymentId, patch) => {
          await membersApi.updatePayment(paymentId, patch);
          await refreshAll();
        }}
      />
    </>
  );
}
